import {Component, Input, OnInit} from '@angular/core';
import {AbstractControl, ControlContainer, NgForm, Validators} from '@angular/forms';
import {
  CmsFlexComponentData
} from '@app/checkout/order-confirmation/order-confirmation-list-item/order-confirmation-order-item.model';
import {DcpUserService} from '@app/services/user/dcp-user.service';
import {JsonService, TransactionListCta, TransactionType, ValidationService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Address, AuthService, CmsService, User} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {InputComponent} from 'ng-design-system';
import {BehaviorSubject, Observable} from 'rxjs';
import {first, take} from 'rxjs/operators';
import {ShareDataConsent, SubmitStatus, UserType} from '../../../models/dcp-user-type.model';
import {DcpUserDomain, LeadFormCreatorModel, LeadFormModel} from '../lead-form.model';

@UntilDestroy()
@Component({
  selector: 'dcp-lead-form-partner-details',
  templateUrl: './lead-form-partner-details.component.html',
  viewProviders: [{provide: ControlContainer, useExisting: NgForm}],
})
export class LeadFormPartnerDetailsComponent implements OnInit {

  @Input() userType: UserType = {isEmployeeLogin: false};
  @Input() shareDataConsent: ShareDataConsent;
  @Input() leadFormModel: LeadFormModel;
  @Input() transactionType: TransactionType;
  @Input() transactionListCta: TransactionListCta;
  @Input() emailValidationRegexp: RegExp;
  @Input() sapEmployeeSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** tracks if the onSubmit function has been called */
  @Input() submitStatus: SubmitStatus;
  @Input() shouldShowContractDetailsColumn: boolean;
  public disableEmailField = false;
  public emailOld: string;
  private emailUser$: Observable<User>;
  private cmsClassificationsProductData: DcpUserDomain;
  cmsComponentId: string = 'ProductCallToActionComponent';

  constructor(
    public authService: AuthService,
    public userService: DcpUserService,
    public userFacade: UserAccountFacade,
    public validationService: ValidationService,
    private cmsService: CmsService,
    private jsonService: JsonService,
  ) {
  }

  ngOnInit() {
    this.mapComponentData();
  }

  /**
   * Fetches component data from cloud commerce.
   */
  mapComponentData() {
    this.cmsService.getComponentData(this.cmsComponentId)
      .pipe(first(), untilDestroyed(this))
      .subscribe((data: CmsFlexComponentData) => {
          this.cmsClassificationsProductData = this.jsonService.parseSafely(data?.classificationsProductData) as DcpUserDomain;
        }
      );
  }

  /**
   * Checks if a validation error exists for the validator name.
   *
   * @param dsInput InputComponent to check for errors.
   * @param validatorName error to look after.
   * @return true if showError is true and the errors array has a value that equals the validatorName.
   */
  hasValidationFailed(dsInput: InputComponent, validatorName: string): boolean {
    if (this.sapEmployeeSubject.value) {
      if (validatorName === 'required') {
        return false;
      }
      if (validatorName === 'email') {
        validatorName = 'pattern';
      }
      this.updatePersonalDetailsValidators(dsInput);
    }
    return this.validationService.hasValidationFailed(dsInput, validatorName, this.submitStatus.submitted);
  }

  /**
   * Fills defaultAddress data into the form if the user is logged in.
   *
   * @param user Form the user service.
   */
  prefillDefaultAddress(user: LeadFormCreatorModel) {
    if (this.userService.userIsEmployee(user.email, this.cmsClassificationsProductData)) {
      this.userType.isEmployeeLogin = true;
    }
    this.disableEmailField = true;
    const address: Address = user.defaultAddress;
    if (address) {
      this.leadFormModel.addressesMap.LTCP.email = user.email || '';
      this.leadFormModel.addressesMap.LTCP.firstName = address.firstName || user.firstName;
      this.leadFormModel.addressesMap.LTCP.lastName = address.lastName || user.lastName;
      this.leadFormModel.addressesMap.LTCP.phone = address.phone;
    } else {
      this.leadFormModel.addressesMap.LTCP.email = user.email || '';
      this.leadFormModel.addressesMap.LTCP.firstName = user.firstName;
      this.leadFormModel.addressesMap.LTCP.lastName = user.lastName;
    }
  }

  /**
   * When focus is getting out of email input - checks whether user for entered email exists
   */
  blurHandler(isFocused: boolean, value: string): void {
    if (isFocused) {
      this.emailOld = value;
    } else if (!this.sapEmployeeSubject.value && value !== this.emailOld) {
      this.emailUser$ = this.userFacade.get();
      this.emailUser$.pipe(take(1), untilDestroyed(this)).subscribe(() => {
        if (this.userService.userIsEmployee(value, this.cmsClassificationsProductData)) {
          this.userType.isEmployeeLogin = true;
          this.submitStatus.isSubmitButtonDisabled = true;
          this.shareDataConsent.isShareDataConsentWarningRed = false;
        } else {
          this.userType.isEmployeeLogin = false;
          this.submitStatus.isSubmitButtonDisabled = false;
        }
      });
    }
  }

  /**
   * When switching to harmony lead form with contract data column, partner details fields become optional,
   * so removing 'required' validator and setting value-oriented ones.
   *
   * @param dsInput an element to be checked
   */
  updatePersonalDetailsValidators(dsInput: InputComponent) {
    const formControl: AbstractControl = dsInput?.formControl;
    formControl?.clearValidators();
    if (dsInput?.fieldName === 'email') {
      formControl?.setValidators([Validators.maxLength(255), Validators.pattern(this.emailValidationRegexp)]);
    } else {
      formControl?.setValidators([Validators.maxLength(255)]);
    }
    formControl?.updateValueAndValidity();
  }
}
